import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdPassword } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { useStateValue } from '../../../../providers/StateProvider';
import { actionTypes } from '../../../../store/reducer';
import { ButtonCancel, ButtonSave, CardFooterCustom, CardIcon } from '../../styles';
import { BodyModal, ButtonSendCode, DescriptionModal, InputCode, MessageError, MessageSuccess, SendCodeMessage, TitleModal } from './styles';
import { checkValidToken, CheckValidTokenResult, requestCodeSign, resendSignatureEmail } from '../../../../services/signature';
import Loading from '../../../../Components/Loading';
import formatTimeRemaining from '../../../../utils/formatTimeRemaining';
import { formatTimeHourMinMS } from '../../../../utils/formatDateMS';
import { maskEmail } from '../../../../utils/maskEmail';
import { cleanupBodyScrollLock } from '../../../../utils/modalCleanup';

type Props = {
    show: boolean;
    setShow: (show: boolean) => void;
};

const ModalCodeValidate = ({ show, setShow }: Props): JSX.Element => {
    const history = useHistory();
    const [code, setCode] = useState<string>('');
    const handleClose = () => {
        setShow(false);
        cleanupBodyScrollLock();
    };
    const [errorInput, setErrorInput] = useState<boolean>(false);
    const [successInput, setSuccessInput] = useState<boolean>(false);
    const [, dispatch] = useStateValue();
    const [tokenStatus, setTokenStatus] = useState<CheckValidTokenResult | null>(null);
    const [checkingToken, setCheckingToken] = useState<boolean>(true);
    const [email, setEmail] = useState<string>('');
    const [resentMessage, setResentMessage] = useState<boolean>(false);


    const handleAssignCode = (e) => {
        const value: string = e.target.value.replace(/\D/g, '');
        setCode(value);
        if (value.length !== 6) {
            setErrorInput(true);
            setSuccessInput(false);
        } else {
            setErrorInput(false);
            setSuccessInput(true);

        }
    }

    const handleRequestCode = async () => {
        const dataStorage = localStorage.getItem('gov_user_data');
        if (!dataStorage) {
            return;
        }
        const userData = JSON.parse(dataStorage);
        const token: string | null = localStorage.getItem('gov_access_token');

        setResentMessage(false);

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (!tokenValidation.hasValidToken) {
            await requestCodeSign(userData.cpf, userData.contato.email, token);
        }
    }

    const handleRequestCodeResend = async () => {
        const dataStorage = localStorage.getItem("gov_user_data");
        const userData = JSON.parse(dataStorage);
        const token: string | null = localStorage.getItem('gov_access_token');

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (tokenValidation.hasValidToken) {
            await resendSignatureEmail(userData.cpf, userData.contato.email, token);
            setResentMessage(true);
            return;
        }

        setResentMessage(false);
    }

    useEffect(() => {
        const dataStorage = localStorage.getItem('gov_user_data');
        if (dataStorage) {
            try {
                const userData = JSON.parse(dataStorage);
                setEmail(userData?.contato?.email);
            } catch (err) {
                console.error(err);
            }
        }
    }, []);

    useEffect(() => {
        if (!show) {
            cleanupBodyScrollLock();
            return;
        }

        const checkTokenAndRequestCode = async () => {
            setCheckingToken(true);
            setResentMessage(false);
            try {
                await handleRequestCode();
            } catch (err) {
                console.error(err);
                setTokenStatus({ hasValidToken: false, message: 'Erro ao verificar token de assinatura.' });
            } finally {
                setCheckingToken(false);
            }
        };

        checkTokenAndRequestCode();
    }, [show]);


    const handleSave = () => {
        history.goBack();
        dispatch({
            type: actionTypes.SET_FLASH_MESSAGE,
            flashMessage: {
                show: true,
                type: 'success-alt',
                title: 'Sucesso',
                message: 'Documento criado!',
            },
        });
    }

    return (
        <>
            <Modal
                size='lg'
                show={show}
                centered
                onHide={handleClose}
                onExited={cleanupBodyScrollLock}
                backdrop
                keyboard
            >
                <Modal.Body>
                    <BodyModal>
                        <CardIcon>
                            <MdPassword />
                        </CardIcon>
                        <TitleModal>Assinatura eletrônica</TitleModal>
                        {checkingToken ? (
                            <Loading />
                        ) : (
                            <>
                                {tokenStatus && tokenStatus.hasValidToken ? (
                                    <DescriptionModal>
                                        {resentMessage ? (
                                            <>Um e-mail com seu token atual foi enviado para <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        ) : (
                                            <>Você possui token válido neste momento. Use o token atual enviado anteriormente para o seu e-mail <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        )}
                                    </DescriptionModal>
                                ) : (
                                    <DescriptionModal>
                                        {tokenStatus?.expiresAt ? (
                                            <>Seu token expirou às <strong>{formatTimeHourMinMS(tokenStatus.expiresAt)}</strong>. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        ) : (
                                            <>Seu token expirou. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        )}
                                    </DescriptionModal>
                                )}

                                <InputCode placeholder='000000' maxLength={6} value={code}
                                    onChange={handleAssignCode}
                                    error={errorInput}
                                    success={successInput}
                                />
                                {errorInput && <MessageError>Código expirado ou inexistente</MessageError>}

                                {successInput && <MessageSuccess>Verificado</MessageSuccess>}
                                <SendCodeMessage>Não recebeu o código? <ButtonSendCode onClick={handleRequestCodeResend}>Reenviar</ButtonSendCode></SendCodeMessage>
                            </>
                        )}
                    </BodyModal>
                </Modal.Body>
                <CardFooterCustom>
                    <ButtonCancel onClick={handleClose}>
                        Cancelar
                    </ButtonCancel>
                    <ButtonSave onClick={handleSave} disabled={!successInput}>
                        Finalizar
                    </ButtonSave>
                </CardFooterCustom>
            </Modal>
        </>
    );
};

export default ModalCodeValidate;
